// client/src/components/moonbeams/SenderProfile.js
import businessProfileService from '../../services/businessProfileService.js';

/**
 * Public-facing sender profile display component.
 * Renders: name, tagline, photo, description, and links.
 * All sections are conditional - only shown if data exists.
 * 
 * Props:
 *   profile: { displayname, tagline, description, photoimageurl }
 *   links: [{ linkid, name, url }]
 *   moonbeamid: number (for tracking link clicks)
 *   recipientid: number (optional, for tracking)
 */
export default function SenderProfile({ profile, links, moonbeamid, recipientid }) {
  if (!profile) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <p className="text-royal-200 text-base">Profile not available</p>
      </div>
    );
  }

  const hasTagline = profile.tagline && profile.tagline.trim();
  const hasPhoto = profile.photoimageurl;
  const hasDescription = profile.description && profile.description.trim();
  const hasLinks = links && links.length > 0;

  const handleLinkClick = async (linkid, url) => {
    // Track the click with moonbeamid for per-card analytics
    try {
      await businessProfileService.trackLinkClick(linkid, moonbeamid, recipientid);
    } catch (err) {
      console.error('Failed to track link click:', err);
    }
    // Open link in new tab
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <div className="bg-royal-800/40 border border-royal-600 rounded-2xl p-6 md:p-8">
        {/* Name - always shown */}
        <h1 className="text-gold font-display text-2xl md:text-3xl text-center mb-2">
          {profile.displayname}
        </h1>

        {/* Tagline - conditional */}
        {hasTagline && (
          <p className="text-royal-200 text-base md:text-lg text-center italic mb-6">
            {profile.tagline}
          </p>
        )}

        {/* Photo + Description section - conditional */}
        {(hasPhoto || hasDescription) && (
          <div className={`mb-6 ${hasPhoto && hasDescription ? 'flex flex-col md:flex-row gap-6' : ''}`}>
            {/* Photo */}
            {hasPhoto && (
              <div className={`flex-shrink-0 ${hasDescription ? 'flex justify-center md:justify-start' : 'flex justify-center'}`}>
                <img
                  src={profile.photoimageurl}
                  alt={profile.displayname}
                  className="w-36 h-36 md:w-44 md:h-44 rounded-xl object-cover shadow-lg"
                />
              </div>
            )}

            {/* Description */}
            {hasDescription && (
              <div className={`flex-1 ${hasPhoto ? 'mt-4 md:mt-0' : ''}`}>
                <p className="text-royal-100 text-base leading-relaxed whitespace-pre-wrap">
                  {profile.description}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Links - conditional */}
        {hasLinks && (
          <div className={`${hasPhoto || hasDescription || hasTagline ? 'pt-4 border-t border-royal-700' : ''}`}>
            <div className={`grid gap-3 ${links.length === 1 ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2'}`}>
              {links.map((link) => (
                <button
                  key={link.linkid}
                  onClick={() => handleLinkClick(link.linkid, link.url)}
                  className="w-full px-5 py-3.5 bg-gold/10 border border-gold/30 rounded-xl text-gold text-base font-medium text-center hover:bg-gold/20 hover:border-gold/50 transition-colors focus:outline-none focus:ring-2 focus:ring-gold/50"
                >
                  {link.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Empty state - only if nothing to show except name */}
        {!hasTagline && !hasPhoto && !hasDescription && !hasLinks && (
          <p className="text-royal-200 text-base text-center mt-4">
            This sender hasn't added profile details yet.
          </p>
        )}
      </div>
    </div>
  );
}